import {
  DueDiligenceItem,
  IDueDiligenceItem,
} from '@app/models/DueDiligenceItem'
import { actionCreators as developmentSiteActions } from '@app/routes/Development/Clients/Sites/View/modules/developmentSiteView'
import { ApplicationState } from '@app/store'
import { errorToast, successToast } from '@app/utils/notifications'
import { makeAuthenticatedRequest, RequestMethod } from '@app/utils/request'
import { List, Record } from 'immutable'
import { Action, Reducer } from 'redux'
import { fork, put, select, take, takeLatest } from 'redux-saga/effects'
import { createSelector } from 'reselect'

export type OnSaveCallbackFunc = (offset: number) => void

// -------------------------
// STATE
// -------------------------
export interface ICriticalDatesEditState {
  readonly error?: string
  readonly offset: number
  readonly isOpen: boolean
  readonly isSaving: boolean
}

const CriticalDatesEditStateRecord = Record({
  offset: 0,
  error: undefined,
  isOpen: false,
  isSaving: false,
})

export class CriticalDatesEditState
  extends CriticalDatesEditStateRecord
  implements ICriticalDatesEditState
{
  public static fromJS(initialState?: Partial<ICriticalDatesEditState>) {
    if (!initialState) {
      return new CriticalDatesEditState()
    }

    const { offset, ...rest } = initialState

    return new CriticalDatesEditState({
      offset,
      ...rest,
    })
  }

  public readonly offset: number
  public readonly error?: string
  public readonly isOpen: boolean
  public readonly isSaving: boolean

  constructor(props?: Partial<CriticalDatesEditState>) {
    props ? super(props) : super()
  }
}

// -------------------------
// ACTIONS
// -------------------------
export enum ActionTypes {
  CloseCriticalDatesEdit = 'CRITICAL_DATES_EDIT::CLOSE',
  ResetState = 'CRITICAL_DATES_EDIT::RESET_STATE',
  SaveCriticalDatesEdit = 'CRITICAL_DATES_EDIT::SAVE_CRITICAL_DATES_EDIT',
  SaveCriticalDatesEditFailure = 'CRITICAL_DATES_EDITD::SAVE_FAILURE',
  SaveCriticalDatesEditRequest = 'CRITICAL_DATES_EDIT::SAVE_REQUEST',
  SaveCriticalDatesEditSuccess = 'CRITICAL_DATES_EDIT::SAVE_SUCCESS',
  SetOffset = 'CRITICAL_DATES_EDIT::SET_OFFSET',
  ShowCriticalDatesEdit = 'CRITICAL_DATES_EDIT::SHOW',
}

interface ICloseCriticalDatesEditAction {
  type: ActionTypes.CloseCriticalDatesEdit
}

interface IResetStateAction {
  type: ActionTypes.ResetState
}

interface ISaveCriticalDatesEditAction {
  offset: number
  criticalDates: List<IDueDiligenceItem>
  type: ActionTypes.SaveCriticalDatesEdit
}

interface ISaveCriticalDatesEditFailureAction {
  error: string
  type: ActionTypes.SaveCriticalDatesEditFailure
}

interface ISaveCriticalDatesEditRequestAction {
  type: ActionTypes.SaveCriticalDatesEditRequest
}

interface ISaveCriticalDatesEditSuccessAction {
  criticalDates: List<IDueDiligenceItem>
  type: ActionTypes.SaveCriticalDatesEditSuccess
}

interface ISetOffsetAction {
  offset: number
  type: ActionTypes.SetOffset
}

interface IShowCriticalDatesEditAction {
  offset: number
  type: ActionTypes.ShowCriticalDatesEdit
}

// Declare a 'discriminated union' type. This guarantees that all references to 'type' properties contain one of the
// declared type strings (and not any other arbitrary string).
type KnownAction =
  | ICloseCriticalDatesEditAction
  | IResetStateAction
  | ISaveCriticalDatesEditFailureAction
  | ISaveCriticalDatesEditRequestAction
  | ISaveCriticalDatesEditSuccessAction
  | ISaveCriticalDatesEditAction
  | ISetOffsetAction
  | IShowCriticalDatesEditAction

// -------------------------
// ACTION CREATORS
// -------------------------
function closeCriticalDatesEdit(): ICloseCriticalDatesEditAction {
  return { type: ActionTypes.CloseCriticalDatesEdit }
}

function resetState(): IResetStateAction {
  return { type: ActionTypes.ResetState }
}

function saveCriticalDatesEdit(
  offset: number,
  criticalDates: List<IDueDiligenceItem>,
): ISaveCriticalDatesEditAction {
  return {
    offset,
    criticalDates,
    type: ActionTypes.SaveCriticalDatesEdit,
  }
}

function saveCriticalDatesEditFailure(
  error: string,
): ISaveCriticalDatesEditFailureAction {
  return {
    error,
    type: ActionTypes.SaveCriticalDatesEditFailure,
  }
}

function saveCriticalDatesEditRequest(): ISaveCriticalDatesEditRequestAction {
  return { type: ActionTypes.SaveCriticalDatesEditRequest }
}

function saveCriticalDatesEditSuccess(
  criticalDates: List<IDueDiligenceItem>,
): ISaveCriticalDatesEditSuccessAction {
  return {
    criticalDates,
    type: ActionTypes.SaveCriticalDatesEditSuccess,
  }
}

function setOffset(offset: number): ISetOffsetAction {
  return {
    offset,
    type: ActionTypes.SetOffset,
  }
}

function showCriticalDatesEdit(offset: number): IShowCriticalDatesEditAction {
  return {
    offset,
    type: ActionTypes.ShowCriticalDatesEdit,
  }
}

export const actionCreators = {
  saveCriticalDatesEditRequest,
  saveCriticalDatesEditSuccess,
  saveCriticalDatesEditFailure,
  closeCriticalDatesEdit,
  resetState,
  saveCriticalDatesEdit,
  showCriticalDatesEdit,
  setOffset,
}

// -------------------------
// REDUCER
// -------------------------
export const reducer: Reducer<CriticalDatesEditState> = (
  state: CriticalDatesEditState = new CriticalDatesEditState(),
  incomingAction: Action,
) => {
  const action = incomingAction as KnownAction
  switch (action.type) {
    case ActionTypes.CloseCriticalDatesEdit:
      return state.set('isOpen', false) as CriticalDatesEditState

    case ActionTypes.ResetState:
      return new CriticalDatesEditState()

    case ActionTypes.SaveCriticalDatesEditFailure:
      return state
        .set('error', action.error)
        .set('isSaving', false) as CriticalDatesEditState

    case ActionTypes.SaveCriticalDatesEdit:
      return state.set('isSaving', true) as CriticalDatesEditState

    case ActionTypes.SaveCriticalDatesEditRequest:
      return state.set('isSaving', true) as CriticalDatesEditState

    case ActionTypes.SaveCriticalDatesEditSuccess:
      return state.set('isSaving', false) as CriticalDatesEditState

    case ActionTypes.SetOffset:
      return state.set('offset', action.offset) as CriticalDatesEditState

    case ActionTypes.ShowCriticalDatesEdit:
      return state.set('isOpen', true) as CriticalDatesEditState

    default:
      // The following line guarantees that every action in the KnownAction union has been covered by a case above
      const exhaustiveCheck: never = action
  }

  return state
}

// -------------------------
// SAGAS
// -------------------------
function* saveCriticalDatesEditSaga(action: ISaveCriticalDatesEditAction) {
  try {
    const reqUrl = `api/dueDiligenceItems/Update/${action.offset}`
    const reqOpts = {
      body: JSON.stringify(
        action.criticalDates
          .filter((date) => !date!.isComplete)
          .map((date) => date!.id),
      ),
      method: RequestMethod.PUT,
    }

    const result = yield makeAuthenticatedRequest(reqUrl, reqOpts)

    const criticalDatesUpdated = result.results.map((result: any) => {
      return DueDiligenceItem.fromJS(result)
    })

    const dates = action.criticalDates
      .map((date) => {
        return (
          criticalDatesUpdated.find(
            (newDate: any) => newDate!.id === date!.id,
          ) || date
        )
      })
      .toList()
    if (result.success === true) {
      yield put(
        successToast(
          'Critical Dates Updated',
          'The submitted updates to critical dates succeeded!',
        ),
      )
      yield put(saveCriticalDatesEditSuccess(dates))
    } else {
      yield put(saveCriticalDatesEditFailure(result.message))
      yield put(errorToast('Oops!', result.message))
    }
  } catch (err) {
    yield put(saveCriticalDatesEditFailure(err.message))
    yield put(errorToast('Oops!', err.message))
  }
}

function* showCriticalDatesEditSaga(action: IShowCriticalDatesEditAction) {
  try {
    // Wait for the quick add to either be saved or the window cancelled
    const nextAction = yield take([
      ActionTypes.SaveCriticalDatesEditSuccess,
      ActionTypes.CloseCriticalDatesEdit,
    ])

    if (nextAction.type === ActionTypes.SaveCriticalDatesEditSuccess) {
      const mState: CriticalDatesEditState = yield select(selectModuleState())
      yield put(closeCriticalDatesEdit())
    }
  } catch (err) {
    yield errorToast('Oops!', err.message)
  }
}

function* watchSaveCriticalDatesEdit() {
  yield takeLatest(ActionTypes.SaveCriticalDatesEdit, saveCriticalDatesEditSaga)
}

function* watchShowCriticalDatesEdit() {
  yield takeLatest(ActionTypes.ShowCriticalDatesEdit, showCriticalDatesEditSaga)
}

export function* criticalDatesEditRootSaga() {
  yield fork(watchSaveCriticalDatesEdit)
  yield fork(watchShowCriticalDatesEdit)
}

// -------------------------
// SELECTORS
// -------------------------
const selectModuleState =
  () =>
  (state: ApplicationState): CriticalDatesEditState =>
    state.criticalDatesEdit

export const selectError = () =>
  createSelector(selectModuleState(), (mState) => mState.error)

export const selectIsOpen = () =>
  createSelector(selectModuleState(), (mState) => mState.isOpen)

export const selectIsSaving = () =>
  createSelector(selectModuleState(), (mState) => mState.isSaving)

export const selectOffset = () =>
  createSelector(selectModuleState(), (mState) => mState.offset)
